import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Plus, X } from "lucide-react";
import { DOCS } from "@/constants/testIds";

const DOC_TYPES = ["УПД", "Акт", "Договор", "Счет-фактура", "Накладная ТОРГ-12"];
const DIRECTIONS = [
  { value: "outgoing", label: "Исходящий — отправить контрагенту" },
  { value: "internal", label: "Внутренний — на согласование в компании" },
  { value: "incoming", label: "Входящий — зарегистрировать полученный документ" },
];

const INITIAL_FORM = {
  direction: "outgoing",
  type: DOC_TYPES[0],
  number: "",
  doc_date: new Date().toISOString().slice(0, 10),
  amount: "",
  vat: "",
  counterparty_id: "",
  counterparty_name: "",
  tags: "",
  project_id: "",
};

export function NewDocumentModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [counterparties, setCounterparties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [showNewCp, setShowNewCp] = useState(false);
  const [newCp, setNewCp] = useState({ name: "", inn: "" });
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [useApproval, setUseApproval] = useState(false);
  const [approvalSteps, setApprovalSteps] = useState([[]]);

  useEffect(() => {
    if (open) {
      api.get("/counterparties").then(({ data }) => setCounterparties(data)).catch(() => {});
      api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
      api.get("/team/members").then(({ data }) => setMembers(data)).catch(() => {});
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm(INITIAL_FORM);
    setFile(null);
    setShowNewCp(false);
    setNewCp({ name: "", inn: "" });
    setSaveAsTemplate(false);
    setUseApproval(false);
    setApprovalSteps([[]]);
  };

  const applyTemplate = (templateId) => {
    const t = templates.find((tpl) => tpl.id === templateId);
    if (!t) return;
    setForm((f) => ({
      ...f,
      direction: t.direction,
      type: t.type,
      counterparty_id: t.counterparty_id || "",
      counterparty_name: t.counterparty_name || "",
      amount: t.amount ?? "",
      vat: t.vat ?? "",
    }));
  };

  const addCounterpartyInline = async () => {
    if (!newCp.name || !newCp.inn) {
      toast.error("Укажите название и ИНН контрагента");
      return;
    }
    try {
      const { data } = await api.post("/counterparties", newCp);
      setCounterparties((prev) => [...prev, data]);
      update("counterparty_id", data.id);
      update("counterparty_name", data.name);
      setShowNewCp(false);
      setNewCp({ name: "", inn: "" });
      toast.success("Контрагент добавлен");
    } catch {
      toast.error("Не удалось добавить контрагента");
    }
  };

  const handleCounterpartyChange = (id) => {
    const cp = counterparties.find((c) => c.id === id);
    update("counterparty_id", id);
    update("counterparty_name", cp?.name || "");
  };

  const toggleStepMember = (stepIdx, memberId, checked) => {
    setApprovalSteps((prev) => {
      const next = [...prev];
      next[stepIdx] = checked ? [...next[stepIdx], memberId] : next[stepIdx].filter((id) => id !== memberId);
      return next;
    });
  };

  const handleSubmit = async (asDraft) => {
    if (!form.number || !form.amount) {
      toast.error("Заполните номер и сумму документа");
      return;
    }
    setBusy(true);
    try {
      let fileData = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fileData = data;
      }
      await api.post("/documents", {
        direction: form.direction,
        type: form.type,
        number: form.number,
        doc_date: form.doc_date,
        amount: parseFloat(form.amount),
        vat: parseFloat(form.vat) || 0,
        counterparty_id: form.counterparty_id || null,
        counterparty_name: form.counterparty_name || null,
        file: fileData,
        as_draft: asDraft,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        project_id: form.project_id || null,
        approval_steps: form.direction === "internal" && useApproval ? approvalSteps.filter((s) => s.length > 0) : null,
      });
      if (saveAsTemplate) {
        await api.post("/templates", {
          name: `${form.type} — ${form.counterparty_name || "без контрагента"}`,
          direction: form.direction,
          type: form.type,
          counterparty_id: form.counterparty_id || null,
          counterparty_name: form.counterparty_name || null,
          amount: parseFloat(form.amount),
          vat: parseFloat(form.vat) || 0,
        });
      }
      toast.success(asDraft ? "Черновик сохранён" : "Документ создан и отправлен на подписание");
      onCreated?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось создать документ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-document-modal">
        <DialogHeader>
          <DialogTitle>Новый документ</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {templates.length > 0 && (
            <div className="space-y-1.5">
              <Label>Использовать шаблон</Label>
              <select data-testid="new-doc-template-select" defaultValue="" onChange={(e) => e.target.value && applyTemplate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Без шаблона</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Направление</Label>
            <select
              data-testid={DOCS.directionSelect}
              value={form.direction}
              onChange={(e) => {
                const dir = e.target.value;
                setForm((f) => ({ ...f, direction: dir, ...(dir === "internal" ? { counterparty_id: "", counterparty_name: "" } : {}) }));
              }}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              {DIRECTIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Тип документа</Label>
              <select data-testid={DOCS.typeSelect} value={form.type} onChange={(e) => update("type", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Номер</Label>
              <Input data-testid={DOCS.numberInput} value={form.number} onChange={(e) => update("number", e.target.value)} placeholder="№" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Дата документа</Label>
              <Input type="date" data-testid={DOCS.dateInput} value={form.doc_date} onChange={(e) => update("doc_date", e.target.value)} />
            </div>
            {form.direction !== "internal" ? (
              <div className="space-y-1.5">
                <Label>Контрагент</Label>
                <select data-testid={DOCS.counterpartyInput} value={form.counterparty_id} onChange={(e) => handleCounterpartyChange(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="">Без контрагента</option>
                  {counterparties.map((cp) => (
                    <option key={cp.id} value={cp.id}>{cp.name}{cp.linked_org_id ? " (в системе)" : ""}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Контрагент</Label>
                <div className="w-full border border-dashed border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-400 bg-slate-50" data-testid="internal-doc-no-counterparty-note">
                  Внутри компании — контрагент не требуется
                </div>
              </div>
            )}
          </div>

          {form.direction !== "internal" && (
            !showNewCp ? (
              <button type="button" onClick={() => setShowNewCp(true)} data-testid="new-doc-add-counterparty-toggle" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Добавить нового контрагента
              </button>
            ) : (
              <div className="flex gap-2 bg-slate-50 rounded-lg p-3">
                <Input placeholder="Название" value={newCp.name} onChange={(e) => setNewCp({ ...newCp, name: e.target.value })} data-testid="new-doc-counterparty-name-input" />
                <Input placeholder="ИНН" value={newCp.inn} onChange={(e) => setNewCp({ ...newCp, inn: e.target.value })} data-testid="new-doc-counterparty-inn-input" />
                <Button size="sm" onClick={addCounterpartyInline} data-testid="new-doc-counterparty-save-button">Добавить</Button>
              </div>
            )
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Сумма с НДС, ₽</Label>
              <Input type="number" data-testid={DOCS.amountInput} value={form.amount} onChange={(e) => update("amount", e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Сумма НДС, ₽</Label>
              <Input type="number" data-testid={DOCS.vatInput} value={form.vat} onChange={(e) => update("vat", e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Теги (через запятую)</Label>
              <Input data-testid="new-doc-tags-input" placeholder="срочно, крупная сумма" value={form.tags} onChange={(e) => update("tags", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Проект</Label>
              <select data-testid="new-doc-project-select" value={form.project_id} onChange={(e) => update("project_id", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Без проекта</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Файл документа (PDF, изображение)</Label>
            <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-500 cursor-pointer hover:border-slate-400">
              <Upload className="w-4 h-4" />
              {file ? file.name : "Загрузить файл"}
              <input type="file" data-testid={DOCS.fileInput} className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.png,.jpg,.jpeg" />
            </label>
          </div>

          {form.direction === "internal" && (
            <div className="space-y-2 border border-slate-200 rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={useApproval}
                  onCheckedChange={(v) => { setUseApproval(v); if (v && approvalSteps.length === 0) setApprovalSteps([[]]); }}
                  data-testid="use-approval-route-checkbox"
                />
                Маршрут согласования (несколько сотрудников по очереди)
              </label>
              {useApproval && (
                <div className="space-y-3 pt-1">
                  {approvalSteps.map((step, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-2.5" data-testid={`approval-step-block-${idx}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-slate-500">Этап {idx + 1}{idx > 0 ? " (после предыдущего)" : ""}</span>
                        {approvalSteps.length > 1 && (
                          <button type="button" onClick={() => setApprovalSteps(approvalSteps.filter((_, i) => i !== idx))} data-testid={`remove-approval-step-button-${idx}`} className="text-slate-400 hover:text-rose-500">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {members.map((m) => (
                          <label key={m.id} className="flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full px-2.5 py-1 cursor-pointer" data-testid={`approval-step-${idx}-member-${m.id}`}>
                            <Checkbox checked={step.includes(m.id)} onCheckedChange={(v) => toggleStepMember(idx, m.id, v)} />
                            {m.name}
                          </label>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">Все выбранные на этапе должны согласовать, затем переход к следующему этапу</p>
                    </div>
                  ))}
                  <button type="button" onClick={() => setApprovalSteps([...approvalSteps, []])} data-testid="add-approval-step-button" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Добавить этап
                  </button>
                </div>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox checked={saveAsTemplate} onCheckedChange={setSaveAsTemplate} data-testid="new-doc-save-template-checkbox" />
            Сохранить как шаблон для повторного использования
          </label>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => handleSubmit(true)} disabled={busy} data-testid={DOCS.saveDraftButton} className="flex-1">
              Сохранить черновик
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={busy} data-testid={DOCS.createSendButton} className="flex-1 bg-slate-900 hover:bg-slate-800">
              Создать и отправить
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
